import LayoutGap from '@/components/Common/LayoutGap';
import Form from '@/components/ui/Form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DeepPartial, useForm, useFormContext } from 'react-hook-form';
import { useState } from 'react';
import { omit } from 'lodash';
import { toast } from 'sonner';

import Button from '@/components/Common/Button';
import Heading, { Size as HeadingSize } from '@/components/Typography/Heading';
import { DisplayValue } from '@/components/Common/DisplayData';
import formatSQLTemplateInput from '@/lib/formatSQLTemplateInput';
import useIssuers from '@/refactored/hooks/useIssuers';
import { AlertDanger, AlertWarning } from '@/components/ui/alert';
import useCreateSegment from '@/refactored/hooks/useSegment';
import { tryCatch } from '@/utils/tryCatch';
import { useOfferById, useUpdateOffer } from '@/refactored/hooks/useOffers';
import { ExperimentResponse } from '@/hooks/api/experiments';
import { useCreateOfferLink, useUpdateOfferLink } from '@/hooks/api/offerLinks';
import {
  completedOfferLinkSchema,
  draftOfferLinkSchema,
  offerLinkSchema,
  type OfferLinkFormValues,
} from './schema';

const OfferFields = () => (
  <Form.Input
    label="Offer ID"
    name="offerId"
    type="text"
    autoComplete="off"
    required
    placeholder="Enter offer ID"
  />
);

const SelectIssuers = () => {
  const [issuers = [], loading, error] = useIssuers({
    shouldFetch: true,
  });

  return (
    <Form.MultiSelect
      label="Issuers"
      name="rules.issuerNames"
      options={issuers.map((issuer) => ({
        label: issuer.issuerName,
        value: issuer.issuerName,
      }))}
      disabled={loading || error}
      placeholder={loading ? 'Loading...' : 'Select issuers...'}
      description={
        error ? 'Error loading issuers' : 'Include users from these issuers.'
      }
    />
  );
};

const ExperimentRulesFields = () => {
  const { watch } = useFormContext();
  const templateType = watch('rules.templateType');
  const maxTransactionCount = watch('rules.templateFields.transactionCount');
  return (
    <>
      <LayoutGap direction="column" size="none">
        <Heading className="m-0" size={HeadingSize.XS}>
          Experiment Targeting Rules
        </Heading>
        <p className="m-0 text-sm text-gray-500">
          Describe the users you want to group this experiment offer.
        </p>
      </LayoutGap>
      <Form.Input
        label="Merchant ID"
        name="rules.merchantId"
        type="text"
        autoComplete="off"
        placeholder="Enter merchant ID"
        description="Include purchases from this merchant."
      />
      <SelectIssuers />
      <Form.DateRange
        label="Users Created Date Range"
        name="rules.userDateRange"
        description="Include users created within this date range."
      />
      <Form.Checkbox
        label="Incrementality"
        name="rules.isIncrementality"
        description="Uncheck to skip propensity scoring."
      />
      <Form.Checkbox
        label="Albert Net New Users Only"
        name="rules.isOnlyAlbertNetNewUsers"
        description="Should only be used for experiments on Albert."
      />
      <Form.Select
        label="Template Type"
        name="rules.templateType"
        options={[
          {
            label: 'New Users',
            value: 'new_users',
          },
          {
            label: 'Active Users',
            value: 'active_users',
          },
          {
            label: 'Lapsed Users',
            value: 'lapsed_users',
          },
        ]}
        placeholder="Select template type"
      />
      {['active_users', 'lapsed_users'].includes(templateType) && (
        <>
          <Form.Input
            label="Max Purchase Count"
            name="rules.templateFields.transactionCount"
            type="number"
            autoComplete="off"
            placeholder="&infin;"
            description="Users with more than this number of purchases within the evaluation duration will be excluded from the experiment."
          />
          <LayoutGap direction="row" className="w-full">
            <Form.Input
              label="Min Evaluation Duration"
              name="rules.templateFields.minEvaluationDurationInMonths"
              type="number"
              autoComplete="off"
              placeholder="0"
              suffix="Months"
              description={
                templateType === 'active_users'
                  ? `Users made ${maxTransactionCount > 1 ? `1-${maxTransactionCount} purchase(s)` : 'at least 1 purchase'} within this duration.`
                  : 'Users made no purchases within this duration.'
              }
            />
            <Form.Input
              label="Max Evaluation Duration"
              name="rules.templateFields.maxEvaluationDurationInMonths"
              type="number"
              autoComplete="off"
              placeholder="&infin;"
              suffix="Months"
              description={
                templateType === 'active_users'
                  ? `${maxTransactionCount > 1 ? `Users no more than ${maxTransactionCount} purchase(s) within this duration.` : ''}`
                  : `Users made ${maxTransactionCount > 0 ? `1-${maxTransactionCount} purchase(s)` : 'at least 1 purchase'} within this duration.`
              }
            />
          </LayoutGap>
        </>
      )}
    </>
  );
};

const GroupUsersFields = ({ experimentId }: { experimentId: string }) => {
  const { getValues } = useFormContext();
  const { offerId, rules } = getValues();
  const { data, error } = formatSQLTemplateInput({
    experimentId,
    merchantName: '',
    merchantId: rules.merchantId,
    offerIds: [offerId],
    segmentDescription: '',
    userDateRange: {
      from: rules.userDateRange.from,
      to: rules.userDateRange.to,
    },
    ...rules,
  });

  return (
    <>
      <DisplayValue
        label="SQL Template Input"
        value={JSON.stringify(data, null, 2)}
        type="code"
      />
      {error && (
        <AlertWarning
          title="SQL Template Input is invalid"
          description="Please check the SQL Template Input on the previous step."
        />
      )}
      <Form.Input
        label="Custom Segment ID"
        type="text"
        name="customSegmentId"
        autoComplete="off"
        placeholder="Enter custom segment ID"
      />
    </>
  );
};

type OfferLinkFormProps = {
  experiment: ExperimentResponse;
  defaultValues?: DeepPartial<OfferLinkFormValues>;
  isEditing?: boolean;
  isActive?: boolean;
  saveOfferLink: typeof useCreateOfferLink | typeof useUpdateOfferLink;
  onSuccess?: (values: OfferLinkFormValues) => void;
};

type FormStep = 'offer' | 'rules' | 'groupUsers';
const OfferLinkForm = ({
  experiment,
  defaultValues = {
    experimentId: experiment?.id,
    offerId: '',
    status: 'DRAFT',
    rules: {
      isIncrementality: true,
      isOnlyAlbertNetNewUsers: false,
      merchantId: '',
      issuerNames: [],
      userDateRange: {},
    },
  },
  saveOfferLink,
  onSuccess,
}: OfferLinkFormProps) => {
  const [formStep, setFormStep] = useState<FormStep>('offer');
  const form = useForm<OfferLinkFormValues>({
    defaultValues,
    resolver: zodResolver(offerLinkSchema),
    mode: 'onChange',
  });

  const [formError, setFormError] = useState<Error | null>(null);
  const [saveAsDraftError, setSaveAsDraftError] = useState<Error | null>(null);

  const [, createSegment] = useCreateSegment();
  const offerId = form.watch('offerId');
  const [, updateOffer] = useUpdateOffer(offerId);

  const [{ data: offer, loading: offerLoading }, getOfferById] = useOfferById(
    offerId,
    {
      manual: true,
    },
  );

  const validateOfferId = async () => {
    const [getOfferByIdError, getOfferByIdResponse] =
      await tryCatch(getOfferById());

    if (getOfferByIdError) {
      return [new Error('Offer not found'), null] as const;
    }

    const { segments, startDate } = getOfferByIdResponse.data;

    const hasSegments = segments && segments.length > 0;
    if (hasSegments) {
      return [new Error('Offer is already linked to a segment'), null] as const;
    }

    const isAfterStartDate = new Date(startDate) < new Date();

    if (isAfterStartDate) {
      return [new Error('Offer start date is in the past'), null] as const;
    }

    if (offer?.status === 'ACTIVE') {
      return [new Error('Offer is already active'), null] as const;
    }

    return [null, offer] as const;
  };

  const handleSubmit = async (values: OfferLinkFormValues) => {
    const { success, data, error } = completedOfferLinkSchema.safeParse({
      ...values,
      status: 'COMPLETED',
    });

    if (error) {
      setFormError(error);
    }

    if (success) {
      const [createSegmentError, createSegmentResponse] = await tryCatch(
        createSegment({
          data: {
            name: `[${experiment.attributes.name}] Test Group: ${data.customSegmentId}`,
            segmentType: 'IMMUTABLE',
            status: 'ACTIVE',
            rules: [
              {
                ruleType: 'user',
                operator: 'in',
                customSegmentId: data.customSegmentId,
              },
            ],
          },
        }),
      );

      if (createSegmentError) {
        toast.error('Failed to create test group segment', {
          description: createSegmentError.message,
        });
      }

      if (createSegmentResponse) {
        toast.success('Test group segment created successfully...');
        // Create Segment Response is a string. We need to extract the segment ID from it.
        const regex = /Created Segment with ID: (.*) that has (\d+) rules./;
        const match = createSegmentResponse.data.match(regex);
        const segmentId = match?.[1];

        if (!segmentId) {
          toast.error('Can not find segment ID');
        }

        if (segmentId && offer) {
          const [updateOfferError, updateOfferData] = await tryCatch(
            updateOffer({
              data: {
                isCustom: false,
                ...omit(
                  offer,
                  '_id',
                  'merchant',
                  'lastModified',
                  'createdDate',
                  'category',
                  'isTargeted',
                  'merchantLocationIds',
                ),
                merchantId: offer.merchant._id,
                segments: [segmentId],
              },
            }),
          );

          if (updateOfferError) {
            setFormError(new Error(`Failed to update offer: ${data.offerId}`));
          }

          if (updateOfferData) {
            toast.success('Test group segment attached to offer...');
            const [saveOfferLinkError, saveOfferLinkResponse] = await tryCatch(
              saveOfferLink({ data }),
            );

            if (saveOfferLinkError) {
              setFormError(new Error(`Failed to save offer link.`));
            }

            toast.success('Offer link saved successfully!');
            onSuccess?.(saveOfferLinkResponse.data);
          }
        }
      }
    }
  };

  const handleNextClick = async () => {
    if (formStep === 'offer') {
      const [error, offer] = await validateOfferId();
      if (error && !offer) {
        setFormError(error);
      } else {
        setFormError(null);
        setFormStep('rules');
      }
    }

    if (formStep === 'rules') {
      setFormStep('groupUsers');
    }
  };

  const handleBackClick = () => {
    if (formStep === 'groupUsers') {
      setFormStep('rules');
    }

    if (formStep === 'rules') {
      setFormStep('offer');
    }
  };

  const [saveAsDraftLoading, setSaveAsDraftLoading] = useState<boolean>(false);
  const saveAsDraft = async () => {
    setSaveAsDraftLoading(true);
    const { success, data, error } = draftOfferLinkSchema.safeParse({
      ...form.getValues(),
      status: 'DRAFT',
    });

    if (error) {
      setSaveAsDraftError(new Error('Failed to save offer link'));
    }

    if (success) {
      const [saveOfferLinkError, saveOfferLinkResponse] = await tryCatch(
        saveOfferLink({ data }),
      );

      if (saveOfferLinkError) {
        setSaveAsDraftError(saveOfferLinkError);
      }

      toast.success('Offer link saved successfully');
      onSuccess?.(saveOfferLinkResponse.data);
    }

    setSaveAsDraftLoading(false);
  };

  return (
    <Form.Provider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
        <LayoutGap>
          <LayoutGap direction="column" className="sticky top-0 flex-grow">
            {formStep === 'offer' && <OfferFields />}
            {formStep === 'rules' && <ExperimentRulesFields />}
            {formStep === 'groupUsers' && (
              <GroupUsersFields experimentId={experiment?.id} />
            )}
            {formError && (
              <AlertDanger
                title="Offer can't be linked"
                description={formError.message}
              />
            )}
            {saveAsDraftError && (
              <AlertDanger
                title="Error Saving as Draft"
                description={saveAsDraftError.message}
              />
            )}
            <LayoutGap className="justify-between">
              <Button
                variant="secondary"
                onClick={() => saveAsDraft()}
                loading={saveAsDraftLoading}
              >
                Save as Draft
              </Button>
              <LayoutGap direction="row" className="self-end" size="small">
                {formStep !== 'offer' && (
                  <Button variant="secondary" onClick={handleBackClick}>
                    Back
                  </Button>
                )}
                {formStep !== 'groupUsers' && (
                  <Button onClick={handleNextClick} loading={offerLoading}>
                    Next
                  </Button>
                )}

                {formStep === 'groupUsers' && (
                  <div className="self-end">
                    <Button
                      type="submit"
                      loading={form.formState.isSubmitting}
                      disabled={saveAsDraftLoading}
                    >
                      Link Offer
                    </Button>
                  </div>
                )}
              </LayoutGap>
            </LayoutGap>
          </LayoutGap>
        </LayoutGap>
      </form>
    </Form.Provider>
  );
};

export default OfferLinkForm;
